import * as RadixLabel from "@radix-ui/react-label";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { cx } from "../internal/cx.js";

export type LabelSize = "sm" | "md" | "lg";

export interface LabelProps extends ComponentPropsWithoutRef<typeof RadixLabel.Root> {
  /**
   * Renders a red asterisk after the label text. Use for inputs that are
   * required by the form schema. Mutually exclusive with `optional`.
   */
  readonly required?: boolean;
  /**
   * Renders a muted "(Optional)" suffix after the label text. Use only
   * inside forms where required is the default and optional fields are
   * the minority — the asymmetric annotation reduces visual noise.
   * Mutually exclusive with `required`.
   */
  readonly optional?: boolean;
  /**
   * Visual size. Matches the size of the input it labels (Input ↔ Label
   * pairs typically use the same size).
   */
  readonly size?: LabelSize;
}

/**
 * Form-control label. Uses Radix Label so click-to-focus binding works
 * for any associated control via `htmlFor` — including custom controls
 * Radix exposes as `role="…"` (Checkbox, Switch, RadioGroup.Item).
 *
 * The cakewalk-mono build adds `required` (red asterisk) and `optional`
 * ("(Optional)") affordances; both default off. Pick one per form.
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>(function Label(
  { className, required, optional, size = "md", children, ...rest },
  ref,
) {
  return (
    <RadixLabel.Root
      ref={ref}
      className={cx("cw-v2-label", `cw-v2-label--${size}`, className)}
      {...rest}
    >
      {children}
      {required ? (
        <span aria-hidden className="cw-v2-label__required">
          *
        </span>
      ) : null}
      {optional && !required ? <span className="cw-v2-label__optional">(Optional)</span> : null}
    </RadixLabel.Root>
  );
});
Label.displayName = "Label";
